import { useTranslation } from "react-i18next";
import UserActivityStatus from "../../../components/user-activity-status/user-activity-status";
import getDateByDiff from "../../../common/utils/calendar/get-date-by-diff";
import InfinityTable from "../../../components/infinity-table/infinity-table";
import { TableCell } from "../../../components/table/table-cell/table-cell";
import classNames from "classnames";
import { useState, useCallback, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectSettings } from "../../../store/slices/settings";
import BulkSelectionBar from "../../../components/table/bulk-selection-bar";
import { useBulkSelection } from "../../../common/utils/useBulkSelection";

const SubjectsList = ({
  subjects,
  fetchUsers,
  hasMore,
  isLoading,
  onHandleRowClick,
  onBulkDelete,
}) => {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState({
    sortField: "last_active",
    sortOrder: "DESC",
  });
  const [isBulkDeleting, setBulkDeleting] = useState(false);
  const tableRef = useRef(null);
  const [bulkLeft, setBulkLeft] = useState(null);

  useEffect(() => {
    const updatePosition = () => {
      if (!tableRef.current) return;
      const rect = tableRef.current.getBoundingClientRect();
      setBulkLeft(rect.left + rect.width / 2);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("resize", updatePosition);
    };
  }, []);

  const {
    selectedKeys,
    clearSelection,
    getSelectionColumn,
    setSelectedKeys,
  } = useBulkSelection(subjects, { rowKey: "id" });

  const handleBulkDeleteClick = useCallback(() => {
    if (!onBulkDelete || selectedKeys.length === 0 || isBulkDeleting) return;

    setBulkDeleting(true);

    onBulkDelete(selectedKeys, {
      onDone: ({ failedIds = [] } = {}) => {
        if (!failedIds.length) {
          clearSelection();
        } else {
          setSelectedKeys(failedIds);
        }

        setBulkDeleting(false);
      },
    });
  }, [
    onBulkDelete,
    selectedKeys,
    isBulkDeleting,
    clearSelection,
    setSelectedKeys,
  ]);

  const getEffectiveLastActivity = (user) =>
    user.lastActivity || user.invitedAt || null;

  const settings = useSelector(selectSettings);
  const sitesEnabled =
    settings?.find((s) => s.key === "sitesEnable")?.value === true;

  const getSortedSubjects = useCallback(() => {
    if (!subjects || !sorting.sortField) return subjects;

    const getComparableValue = (val) => {
      if (val === null || val === undefined) return "";
      if (typeof val === "string") {
        const parsed = Date.parse(val);
        return isNaN(parsed) ? val.toLowerCase() : parsed;
      }
      return val;
    };

    const sorted = [...subjects].sort((a, b) => {
      let aVal;
      let bVal;

      if (sorting.sortField === "last_active") {
        aVal = getComparableValue(getEffectiveLastActivity(a));
        bVal = getComparableValue(getEffectiveLastActivity(b));
      } else {
        aVal = getComparableValue(a[sorting.sortField]);
        bVal = getComparableValue(b[sorting.sortField]);
      }

      if (aVal < bVal) return sorting.sortOrder === "ASC" ? -1 : 1;
      if (aVal > bVal) return sorting.sortOrder === "ASC" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [subjects, sorting]);

  const updateSorting = useCallback((sortField, sortOrder) => {
    setSorting({ sortField, sortOrder });
  }, []);

  const onChange = (pagination, filters, sorter) => {
    const sortField = sorter.field;
    let sortOrder;

    if (!sorter.order) {
      sortOrder = "ASC";
    } else {
      sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    }
    updateSorting(sortField, sortOrder);
  };

  const tableColumns = [
    // Rows are only selected to be deleted together.
    ...(onBulkDelete ? [getSelectionColumn()] : []),
    {
      title: t("subject_management.table.subjectId"),
      dataIndex: "id",
      render: (_, user) => <TableCell title={user.displayId || "N/A"} />,
      key: "id",
      width: "20%",
      sorter: true,
    },
    {
      title: t("subject_management.table.cohort"),
      dataIndex: "cohort",
      render: (_, user) => <TableCell text={user.cohort || "N/A"} />,
      key: "cohort",
      width: "25%",
      sorter: true,
    },
    ...(sitesEnabled
      ? [
          {
            title: t("subject_management.table.sites"),
            dataIndex: "sites",
            key: "sites",
            render: (_, user) => <TableCell text={user.siteName || "N/A"} />,
            width: "20%",
          },
        ]
      : []),
    {
      title: t("subject_management.table.status"),
      dataIndex: "status",
      key: "status",
      render: (_, user) => (
        <div className={classNames("lf-table-cell", "lf-status-stack")}>
          <UserActivityStatus userStatus={user.status} />
          {user.personStatus === "blocked" && (
            <UserActivityStatus userStatus="blocked" secondary />
          )}
        </div>
      ),
      width: "20%",
      sorter: true,
    },
    {
      title: t("subject_management.table.last_active"),
      dataIndex: "last_active",
      key: "last_active",
      render: (_, user) => (
        <TableCell text={getDateByDiff(getEffectiveLastActivity(user))} />
      ),
      sorter: true,
    },
  ];

  return (
    <InfinityTable
      data={getSortedSubjects()}
      fetchData={fetchUsers}
      hasMore={hasMore}
      tableColumns={tableColumns}
      isLoading={isLoading}
      onHandleRowClick={onHandleRowClick}
      onChange={onChange}
      rowTestID={(subject) => `subject-row-${subject.id}`}
      containerRef={tableRef}
      after={
        <BulkSelectionBar
          left={bulkLeft}
          count={selectedKeys.length}
          onClear={clearSelection}
          onDelete={handleBulkDeleteClick}
          loading={isBulkDeleting}
        />
      }
    />
  );
};

export default SubjectsList;